import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';

const CACHE_TTL_MS = 15 * 60 * 1000;

interface PredictionLike {
  isScored?: boolean;
}

const hasAnyScored = (predictions: PredictionLike[]) =>
  predictions.some((prediction) => prediction.isScored);

const readCachedPredictions = (userId: string): PredictionLike[] | null => {
  const summaryRaw = sessionStorage.getItem('my_predictions_summary_data');
  const summaryTs = sessionStorage.getItem('my_predictions_summary_data_ts');
  if (summaryRaw && summaryTs && Date.now() - parseInt(summaryTs, 10) < CACHE_TTL_MS) {
    try {
      const parsed = JSON.parse(summaryRaw);
      if (Array.isArray(parsed.predictions)) return parsed.predictions;
    } catch {
      // ignore invalid cache
    }
  }

  const timelineRaw = sessionStorage.getItem(`prediction_timeline_all_${userId}`);
  const timelineTs = sessionStorage.getItem(`prediction_timeline_timestamp_all_${userId}`);
  if (timelineRaw && timelineTs && Date.now() - parseInt(timelineTs, 10) < CACHE_TTL_MS) {
    try {
      const parsed = JSON.parse(timelineRaw);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // ignore invalid cache
    }
  }

  return null;
};

export const useHasScoredPredictions = (): { hasScoredPredictions: boolean; loading: boolean } => {
  const { user } = useAuth();
  const [hasScoredPredictions, setHasScoredPredictions] = useState(false);
  const [loading, setLoading] = useState(Boolean(user));

  useEffect(() => {
    if (!user) {
      setHasScoredPredictions(false);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const check = async () => {
      const cached = readCachedPredictions(user.id);
      if (cached) {
        if (!cancelled) {
          setHasScoredPredictions(hasAnyScored(cached));
          setLoading(false);
        }
        return;
      }

      try {
        const response = await apiClient.get('/predictions');
        if (!cancelled) {
          setHasScoredPredictions(hasAnyScored(response.data || []));
        }
      } catch {
        if (!cancelled) {
          setHasScoredPredictions(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    setLoading(true);
    check();

    return () => {
      cancelled = true;
    };
  }, [user]);

  return { hasScoredPredictions, loading };
};
